import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer with support for:
 * - Section rendering after AJAX cart updates
 * - Persistent cart attributes (custom fields)
 * - Sticky summary state
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController|null} */
  #fetchController = null;

  /** @type {MutationObserver|null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.#observeDrawerUpdates();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.#observer?.disconnect();
    this.#observer = null;
    this.#fetchController?.abort();
    this.#fetchController = null;
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  open() {
    this.showDialog();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  // ---------------------------------------------------------------------------
  // Cart add handler
  // ---------------------------------------------------------------------------

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  // ---------------------------------------------------------------------------
  // Section rendering — aggiorna il drawer via fetch e ri-inietta il campo
  // ---------------------------------------------------------------------------

  /**
   * Richiede il re-render della section del drawer tramite Shopify Section Rendering API.
   * Preserva il valore del campo custom prima del re-render e lo ripristina dopo.
   *
   * @param {string} sectionId - L'ID della section Liquid del drawer (es. 'cart-drawer')
   */
  async refreshSection(sectionId = 'cart-drawer') {
    // 1. Salva il valore del campo custom prima del re-render
    const customFieldValue = this.#readCustomField();

    // 2. Persisti il valore lato server prima di aggiornare il DOM
    if (customFieldValue) {
      await this.#persistCartAttribute('note_personalizzata', customFieldValue);
    }

    // 3. Abort eventuale fetch precedente
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    try {
      const response = await fetch(
        `${window.Shopify.routes.root}?sections=${sectionId}`,
        { signal: this.#fetchController.signal }
      );

      if (!response.ok) throw new Error(`Section fetch failed: ${response.status}`);

      const data = await response.json();
      const html = data[sectionId];

      if (!html) throw new Error(`Section "${sectionId}" not found in response`);

      // 4. Sostituisci il contenuto del drawer
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newContent = doc.querySelector('.cart-drawer__content');
      const currentContent = this.querySelector('.cart-drawer__content');

      if (newContent && currentContent) {
        currentContent.replaceWith(newContent);
      }

      // 5. Ripristina il valore del campo nel DOM aggiornato
      this.#writeCustomField(customFieldValue);

      // 6. Aggiorna lo stato sticky dopo il re-render
      this.#updateStickyState();

    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('[CartDrawer] Errore nel refresh della section:', err);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Custom field — lettura e scrittura nel DOM
  // ---------------------------------------------------------------------------

  /**
   * Legge il valore del campo custom dal DOM del drawer.
   * @returns {string}
   */
  #readCustomField() {
    const field = this.querySelector('[name="attributes[note_personalizzata]"]');
    return field?.value ?? '';
  }

  /**
   * Scrive il valore del campo custom nel DOM del drawer dopo il re-render.
   * @param {string} value
   */
  #writeCustomField(value) {
    if (!value) return;
    const field = this.querySelector('[name="attributes[note_personalizzata]"]');
    if (field) field.value = value;
  }

  // ---------------------------------------------------------------------------
  // Persistenza lato server — cart.attributes via /cart/update.js
  // ---------------------------------------------------------------------------

  /**
   * Persiste un attributo del carrello lato server.
   * Il valore sopravvive a qualsiasi re-render successivo.
   *
   * @param {string} key
   * @param {string} value
   */
  async #persistCartAttribute(key, value) {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { [key]: value } }),
      });
    } catch (err) {
      console.error('[CartDrawer] Errore nel persistere l\'attributo carrello:', err);
    }
  }

  // ---------------------------------------------------------------------------
  // MutationObserver — intercetta re-render parziali del drawer
  // ---------------------------------------------------------------------------

  /**
   * Osserva il drawer per re-render parziali (es. aggiunta/rimozione prodotti).
   * Quando il DOM cambia, ripristina il campo custom se necessario.
   */
  #observeDrawerUpdates() {
    const target = this.querySelector('.cart-drawer__content') ?? this;

    this.#observer = new MutationObserver((mutations) => {
      const structuralChange = mutations.some(
        (m) => m.type === 'childList' && m.addedNodes.length > 0
      );

      if (!structuralChange) return;

      // Il campo è ancora presente con il suo valore?
      const field = this.querySelector('[name="attributes[note_personalizzata]"]');
      if (field && field.value) return; // tutto ok

      // Campo perso o svuotato: recupera il valore dal server
      this.#restoreFieldFromServer();
    });

    this.#observer.observe(target, { childList: true, subtree: true });
  }

  /**
   * Recupera il valore dell'attributo dal server e lo ripristina nel campo.
   */
  async #restoreFieldFromServer() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const savedValue = cart.attributes?.note_personalizzata ?? '';
      this.#writeCustomField(savedValue);
    } catch (err) {
      console.error('[CartDrawer] Errore nel ripristino del campo:', err);
    }
  }

  // ---------------------------------------------------------------------------
  // Sticky summary
  // ---------------------------------------------------------------------------

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
